// シフト管理関連の型定義

export type ShiftCategory = 'night_available' | 'night_only_part' | 'day_early_only' | 'short_part' | 'fulltime_part';

export const SHIFT_CATEGORY_LABELS: Record<ShiftCategory, string> = {
  night_available: '夜勤あり正社員',
  night_only_part: '夜勤専従パート',
  day_early_only: '早番/日勤のみ',
  short_part: '時短パート',
  fulltime_part: '8時間パート',
};

export type DayOfWeek = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export const DAY_OF_WEEK_LABELS: Record<DayOfWeek, string> = {
  mon: '月', tue: '火', wed: '水', thu: '木', fri: '金', sat: '土', sun: '日',
};

// Excelの勤務タイプに合わせた定義
export type ShiftType = 'day' | 'early' | 'late' | 'late_special' | 'night' | 'after_night' | 'dayoff' | 'paid' | 'am' | 'pm' | 'on_call';

export const SHIFT_TYPE_LABELS: Record<ShiftType, string> = {
  day: '日',
  early: '早',
  late: '遅',
  late_special: '特遅',
  night: '夜',
  after_night: '明',
  dayoff: '公',
  paid: '有',
  am: 'Ａ',
  pm: 'Ｐ',
  on_call: '宿',
};

export const SHIFT_TYPE_COLORS: Record<ShiftType, string> = {
  day: 'bg-blue-100 text-blue-800',
  early: 'bg-green-100 text-green-800',
  late: 'bg-orange-100 text-orange-800',
  late_special: 'bg-red-100 text-red-800',
  night: 'bg-purple-100 text-purple-800',
  after_night: 'bg-indigo-100 text-indigo-800',
  dayoff: 'bg-gray-100 text-gray-600',
  paid: 'bg-yellow-100 text-yellow-800',
  am: 'bg-cyan-100 text-cyan-800',
  pm: 'bg-teal-100 text-teal-800',
  on_call: 'bg-pink-100 text-pink-800',
};

export type PreferenceType = 'paid' | 'dayoff' | 'night_request';

export const PREFERENCE_TYPE_LABELS: Record<PreferenceType, string> = {
  paid: '有給',
  dayoff: '公休',
  night_request: '夜勤希望',
};

// DB行 → フロントエンドモデル

export interface EmployeeShiftCondition {
  id: string;
  employeeId: string;
  shiftCategory: ShiftCategory;
  nightShiftMaxPerMonth: number | null;
  nightShiftAllowedDays: DayOfWeek[] | null;
  workDaysPerWeek: number | null;
  regularOffDays: DayOfWeek[] | null;
  monthlyWeekendWork: number;
  weekendWorkAllowedDays: DayOfWeek[] | null;
  allowedShiftTypes: ShiftType[] | null;
  fixedWorkStart: string | null;
  fixedWorkEnd: string | null;
  workHoursPerDay: number | null;
  notes: string | null;
}

export interface ShiftPreference {
  id: string;
  employeeId: string;
  targetYear: number;
  targetMonth: number;
  preferenceDate: string; // YYYY-MM-DD
  preferenceType: PreferenceType;
  notes: string | null;
}

export interface ShiftSchedule {
  id: string;
  employeeId: string;
  scheduleDate: string; // YYYY-MM-DD
  shiftType: ShiftType;
  isAutoGenerated: boolean;
  isManuallyEdited: boolean;
}

export interface ShiftSettings {
  id: string;
  deadlineDay: number;
  minNight: number;
  minEarly: number;
  idealEarly: number;
  minDay: number;
  idealDay: number;
  targetJobType: string;
  monthlyOffDays31: number;
  monthlyOffDaysOther: number;
}

// Supabase行のスネークケース→キャメルケース変換ヘルパー

export function toShiftCondition(row: Record<string, unknown>): EmployeeShiftCondition {
  return {
    id: String(row.id),
    employeeId: String(row.employee_id),
    shiftCategory: row.shift_category as ShiftCategory,
    nightShiftMaxPerMonth: row.night_shift_max_per_month as number | null,
    nightShiftAllowedDays: row.night_shift_allowed_days as DayOfWeek[] | null,
    workDaysPerWeek: row.work_days_per_week as number | null,
    regularOffDays: row.regular_off_days as DayOfWeek[] | null,
    monthlyWeekendWork: (row.monthly_weekend_work as number) ?? 0,
    weekendWorkAllowedDays: row.weekend_work_allowed_days as DayOfWeek[] | null,
    allowedShiftTypes: row.allowed_shift_types as ShiftType[] | null,
    fixedWorkStart: row.fixed_work_start as string | null,
    fixedWorkEnd: row.fixed_work_end as string | null,
    workHoursPerDay: row.work_hours_per_day as number | null,
    notes: row.notes as string | null,
  };
}

export function toShiftPreference(row: Record<string, unknown>): ShiftPreference {
  return {
    id: String(row.id),
    employeeId: String(row.employee_id),
    targetYear: row.target_year as number,
    targetMonth: row.target_month as number,
    preferenceDate: String(row.preference_date),
    preferenceType: row.preference_type as PreferenceType,
    notes: row.notes as string | null,
  };
}

export function toShiftSchedule(row: Record<string, unknown>): ShiftSchedule {
  return {
    id: String(row.id),
    employeeId: String(row.employee_id),
    scheduleDate: String(row.schedule_date),
    shiftType: row.shift_type as ShiftType,
    isAutoGenerated: !!row.is_auto_generated,
    isManuallyEdited: !!row.is_manually_edited,
  };
}

export function toShiftSettings(row: Record<string, unknown>): ShiftSettings {
  return {
    id: String(row.id),
    deadlineDay: (row.deadline_day as number) ?? 15,
    minNight: (row.min_night as number) ?? 2,
    minEarly: (row.min_early as number) ?? 2,
    idealEarly: (row.ideal_early as number) ?? 3,
    minDay: (row.min_day as number) ?? 1,
    idealDay: (row.ideal_day as number) ?? 2,
    targetJobType: (row.target_job_type as string) ?? '介護',
    monthlyOffDays31: (row.monthly_off_days_31 as number) ?? 9,
    monthlyOffDaysOther: (row.monthly_off_days_other as number) ?? 8,
  };
}
